import React, { useEffect, useState, useCallback } from 'react';
import { getClients, createClient, updateClient, deleteClient, exportClientsCSV } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, Pencil, Trash2, Search, Building2, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const INDUSTRIES = ['Technology', 'Healthcare', 'Finance', 'E-commerce', 'Education', 'Real Estate', 'Food & Beverage', 'Other'];

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', address: '', industry: '', notes: '', status: 'active' });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await getClients();
      setClients(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', company: '', email: '', phone: '', address: '', industry: '', notes: '', status: 'active' });
    setDialogOpen(true);
  };

  const openEdit = (client) => {
    setEditing(client);
    setForm({ name: client.name, company: client.company, email: client.email, phone: client.phone || '', address: client.address || '', industry: client.industry || '', notes: client.notes || '', status: client.status || 'active' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await updateClient(editing.id, form);
        toast.success('Client updated');
      } else {
        await createClient(form);
        toast.success('Client created');
      }
      setDialogOpen(false);
      fetchClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error saving client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await deleteClient(id);
      toast.success('Client deleted');
      fetchClients();
    } catch (e) {
      toast.error('Error deleting client');
    }
  };

  const filtered = clients.filter((c) =>
    [c.name, c.company, c.email, c.industry].some((f) => (f || '').toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) {
    return <div className="space-y-4" data-testid="clients-loading"><div className="h-8 bg-zinc-100 animate-pulse w-48" /><div className="h-64 bg-zinc-100 animate-pulse" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">Clients</h1>
        <div className="flex items-center gap-2">
          <a href={exportClientsCSV()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 h-9 border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 transition-all duration-150" data-testid="export-clients-csv">
            <Download size={14} /> Export CSV
          </a>
          <Button onClick={openCreate} className="rounded-none bg-black text-white hover:bg-zinc-800 gap-2" data-testid="add-client-button">
            <Plus size={16} /> Add Client
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          placeholder="Search clients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 rounded-none border-zinc-300"
          data-testid="client-search-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="clients-table">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Name</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Company</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Email</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Industry</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Status</th>
              {isAdmin && <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Created By</th>}
              <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={isAdmin ? 7 : 6} className="text-center py-12 text-zinc-400">No clients found</td></tr>
            ) : (
              filtered.map((c) => (
                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150 cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-zinc-900 flex items-center gap-2">
                    <Building2 size={14} className="text-zinc-400" />{c.name}
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{c.company}</td>
                  <td className="px-4 py-3 text-zinc-600">{c.email}</td>
                  <td className="px-4 py-3 text-zinc-600">{c.industry || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium ${c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>
                      {c.status}
                    </span>
                  </td>
                  {isAdmin && <td className="px-4 py-3 text-zinc-500 text-xs">{c.created_by_name || '-'}</td>}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="h-8 w-8 p-0" data-testid={`edit-client-${c.id}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-client-${c.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg rounded-none border-zinc-200" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold tracking-tight">{editing ? 'Edit Client' : 'New Client'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-none" data-testid="client-name-input" required />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Company</Label>
                <Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} className="rounded-none" data-testid="client-company-input" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-none" data-testid="client-email-input" required />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="rounded-none" data-testid="client-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Industry</Label>
                <Select value={form.industry} onValueChange={(val) => setForm({...form, industry: val})}>
                  <SelectTrigger className="rounded-none" data-testid="client-industry-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((ind) => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Status</Label>
                <Select value={form.status} onValueChange={(val) => setForm({...form, status: val})}>
                  <SelectTrigger className="rounded-none" data-testid="client-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Address</Label>
              <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="rounded-none" data-testid="client-address-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="rounded-none" rows={3} data-testid="client-notes-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none border-zinc-300" data-testid="client-cancel-button">Cancel</Button>
              <Button onClick={handleSave} disabled={saving || !form.name || !form.company || !form.email} className="rounded-none bg-black text-white hover:bg-zinc-800" data-testid="client-save-button">
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
